import React, { useState } from "react";
import BounceLoader from "react-spinners/BounceLoader";
import { Collapse } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import AxiosClient from "../../../../../../../../Network/AxiosClient";
import { useNotification } from "../../../../../../../Notifications/NotificationProvider";
import { env } from "../../../../../../../../utilities/config";
import { FIELD_FORCE_STATUS_DEACTIVATED } from "../../../../../../../UserList/utils/userConfigs";

const BlacklistActionModal = ({
  data,
  isBulk = false,
  fieldForceType = "mdo",
  fieldForceStatus,
  action,
  onConfirm,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const dispatchNotification = useNotification();

  const isBlacklistMode = action === "blacklist";
  const endpoint = isBlacklistMode
    ? `blacklist-${fieldForceType}`
    : `unblacklist-${fieldForceType}`;
  const payloadIdsKey = `${fieldForceType}_ids`;

  const handleSingleAction = async () => {
    setLoading(true);

    try {
      const payload = { [payloadIdsKey]: [data.id] };
      const res = await AxiosClient.post(
        `${env.API_URL}/admin_panel/v1/${endpoint}`,
        payload
      );
      const successfulCount = res?.data?.successful || [];
      const skippedCount = res?.data?.skipped || [];
      const notFoundCount = res?.data?.not_found || [];
      if (successfulCount > 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: `${isBlacklistMode ? "Blacklisted" : "Unblacklisted"} successfully`,
        });
      } else if (skippedCount > 0) {
        dispatchNotification({
          type: "ERROR",
          message: `Action skipped for employee`,
        });
      } else if (notFoundCount > 0) {
        dispatchNotification({ type: "ERROR", message: `Employee not found` });
      } else {
        dispatchNotification({
          type: "SUCCESS",
          message: `Action completed without any changes.`,
        });
      }
      onConfirm();
    } catch (error) {
      dispatchNotification({
        type: "ERROR",
        message: error?.response?.data?.message || `Failed to ${action} user.`,
      });
      onCancel();
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async () => {
    setLoading(true);
    const payloadIds = data.map((item) => item[`${fieldForceType}_id`]);
    try {
      const payload = {
        [payloadIdsKey]: payloadIds,
      };

      const res = await AxiosClient.post(
        `${env.API_URL}/admin_panel/v1/${endpoint}`,
        payload
      );

      const successful = res?.data?.successful || [];
      const skipped = res?.data?.skipped || [];
      const notFound = res?.data?.not_found || [];

      if (successful.length > 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: `${successful.length} employee${successful.length > 1 ? "s" : ""} ${isBlacklistMode ? "blacklisted" : "unblacklisted"} successfully.`,
        });
      }

      if (skipped.length > 0) {
        dispatchNotification({
          type: "WARNING",
          message: `${skipped.length} employee${skipped.length > 1 ? "s were" : " was"} skipped.`,
        });
      }

      if (notFound.length > 0) {
        dispatchNotification({
          type: "ERROR",
          message: `${notFound.length} employee${notFound.length > 1 ? "s were" : " was"} not found.`,
        });
      }

      if (
        successful.length === 0 &&
        skipped.length === 0 &&
        notFound.length === 0
      ) {
        dispatchNotification({
          type: "SUCCESS",
          message: `Bulk ${action} request completed without any changes.`,
        });
      }

      onConfirm();
    } catch (error) {
      let errorMessage =
        error?.response?.data?.message ||
        `Failed to ${action} user${isBulk ? "s" : ""}.`;
      if (
        errorMessage[payloadIdsKey] &&
        Array.isArray(errorMessage[payloadIdsKey]) &&
        errorMessage[payloadIdsKey].length > 0
      ) {
        errorMessage = errorMessage[payloadIdsKey][0];
      }
      dispatchNotification({ type: "ERROR", message: errorMessage });
      onCancel();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (isBulk) {
      await handleBulkAction();
    } else {
      await handleSingleAction();
    }
  };
  const employeeName = data?.name || "this employee";
  const selectedCount = Array.isArray(data) ? data.length : 0;
  const isDeactivated =
    fieldForceStatus.toLowerCase() ===
    FIELD_FORCE_STATUS_DEACTIVATED.toLowerCase();

  const employeeLabel = `employee${selectedCount === 1 ? "" : "s"}`;

  const getModalText = () => {
    const baseMessage = isBulk
      ? `Are you sure you want to ${action} ${selectedCount} selected ${employeeLabel}?`
      : `${isDeactivated ? "This employee is deactivated. " : ""}Are you sure you want to ${action} ${employeeName}?`;

    return (
      baseMessage +
      (action === "blacklist"
        ? ` Their${isDeactivated ? "" : " account will be deactivated and their"} identification document will be blocked.`
        : " This will unblock their identification document. You can then reactivate the account from the next screen.")
    );
  };
  return (
    <div className={isBlacklistMode ? "blacklist-view" : "unblacklist-view"}>
      <Collapse in={true}>
        <Alert
          severity={isBlacklistMode ? "warning" : "info"}
          style={{ fontSize: 18 }}
        >
          {getModalText()}
        </Alert>

        <div className="footer-edit">
          <BounceLoader
            className="loader"
            color={"#e2136e"}
            size={20}
            loading={loading}
          />
          <button className="button--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={isBlacklistMode ? "button--danger" : "button--confirm"}
            onClick={handleConfirm}
            disabled={loading}
          >
            Confirm
          </button>
        </div>
      </Collapse>
    </div>
  );
};

export default BlacklistActionModal;
